/**
 * `fetchRaidBoard` — pulls the raid-snapshot payload for a given raid
 * and stages the rooms / groups / players into the raid-mode refs.
 * Lives outside the slim composer because the body is large and reads
 * every raid ref directly.
 *
 * The slim composer invokes this through a closure bound to the
 * `useRaidActions` composable's `fetchRaidBoard` ctx slot.
 */
import type { Ref } from 'vue'

import { resolveClient } from '@/shared/ports/resolveClient'
import type { RaidBoardQuery } from '../../lib/handWrittenGraphql'
import { applyRaidBoardSnapshot } from './applySnapshot'
import { mapRaidStageDiffs } from './stageDiffs'
import type {
  RaidBoardPayload,
  RaidGroup,
  RaidPlayer,
  RaidRoom,
  RaidStage,
  RaidStageDiff,
  RaidStageId,
} from '../../types'

// Local alias — see `stores/board/actions.ts` for the rationale.
const apolloClient = resolveClient()

export interface FetchRaidBoardRefs {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  /**
   * Incremented before a subscription snapshot applies. A selection query
   * that started before that authoritative state must not overwrite it when
   * its HTTP response arrives later.
   */
  readonly liveSnapshotGeneration: Ref<number>
  readonly activeStageId: Ref<RaidStageId | null>
  readonly raidRooms: Ref<ReadonlyArray<RaidRoom>>
  readonly raidGroups: Ref<ReadonlyArray<RaidGroup>>
  readonly raidPlayers: Ref<ReadonlyArray<RaidPlayer>>
  readonly raidStages: Ref<ReadonlyArray<RaidStage>>
  readonly raidStageDiffs: Ref<ReadonlyArray<RaidStageDiff>>
}

export interface FetchRaidBoardApi {
  readonly fetchRaidBoard: (raidId: string, shouldApply?: () => boolean) => Promise<boolean>
}

export function useFetchRaidBoard(
  refs: FetchRaidBoardRefs,
  raidActiveBoard: Ref<RaidBoardPayload | null>,
): FetchRaidBoardApi {
  async function fetchRaidBoard(raidId: string, shouldApply: () => boolean = () => true): Promise<boolean> {
    const snapshotGeneration = refs.liveSnapshotGeneration.value
    try {
      const { RaidBoardDocument } = await import('../../lib/handWrittenGraphql')
      const stageParam =
        refs.activeStageId.value === null ? null : Number(refs.activeStageId.value)
      const res = await apolloClient.query<RaidBoardQuery>({
        query: RaidBoardDocument,
        variables: {
          guildId: refs.activeDiscordGuildId.value,
          raidId: Number(raidId),
          stageId: stageParam,
        } as never,
        fetchPolicy: 'network-only',
      })
      const boardPayload = res.data?.raidBoard
      if (!boardPayload) {
        if (!shouldApply()) return false
        if (snapshotGeneration !== refs.liveSnapshotGeneration.value) return true
        refs.error.value = 'The selected raid is no longer available.'
        return false
      }
      if (!shouldApply()) return false
      // The subscription has already replaced the selected raid state. This
      // read succeeded, but it is no longer allowed to write stale data or
      // make the selection lifecycle roll back its newer snapshot.
      if (snapshotGeneration !== refs.liveSnapshotGeneration.value) return true
      const responseStageId = boardPayload.selectedStageId === null
        ? null
        : String(boardPayload.selectedStageId)
      if (responseStageId !== refs.activeStageId.value) return true
      applyRaidBoardSnapshot(
        {
          raidRooms: refs.raidRooms,
          raidGroups: refs.raidGroups,
          raidPlayers: refs.raidPlayers,
          activeRaidBoard: raidActiveBoard,
        },
        boardPayload,
      )
      refs.raidStageDiffs.value = mapRaidStageDiffs(
        boardPayload.stageDiffs,
        refs.raidStages.value,
        boardPayload,
      )
      return true
    } catch (e: unknown) {
      if (!shouldApply()) return false
      if (snapshotGeneration !== refs.liveSnapshotGeneration.value) return true
      refs.error.value = e instanceof Error ? e.message : String(e)
      return false
    }
  }

  return { fetchRaidBoard }
}
